import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Warning } from '@react-apps/design-system';
import { Tooltip } from '@/shared/components/tooltip';
import { useUser } from '../hooks/use-user';
import { useUserDuplicateDocument } from '../hooks/use-user-duplicate-document';

/**
 * Ícone de alerta ao lado do documento quando ele se repete em outra pessoa;
 * o clique abre a lista delas. Some sem papel de admin, sem duplicidade,
 * carregando ou com erro: é um sinal a mais, não um estado da página.
 */
export function DuplicateDocumentBadge({ userId }: { userId: string }) {
  const { data } = useUserDuplicateDocument(userId);
  const [open, setOpen] = useState(false);
  const others = data?.otherUserIds ?? [];
  if (others.length === 0) return null;

  const label = `Também em ${others.length === 1 ? 'outra pessoa' : `${others.length} pessoas`}`;
  const close = () => setOpen(false);

  return (
    <>
      <Tooltip trigger="hover" message={label}>
        <button
          type="button"
          aria-label={label}
          onClick={() => setOpen(true)}
          className="inline-flex shrink-0 items-center text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300"
        >
          <Warning size="md" />
        </button>
      </Tooltip>
      <Modal
        open={open}
        onClose={close}
        title="Mesmo documento em outras pessoas"
        size="sm"
      >
        <ul className="divide-(--color-border) divide-y">
          {others.map((id) => (
            <OtherUserRow key={id} userId={id} onNavigate={close} />
          ))}
        </ul>
      </Modal>
    </>
  );
}

/**
 * Só monta com o modal aberto, então o nome é buscado só aí — o grupo é
 * pequeno, uma query por pessoa basta. Fecha o modal ao navegar: a página de
 * detalhe é a mesma rota, e o estado `open` sobreviveria à troca de pessoa.
 */
function OtherUserRow({
  userId,
  onNavigate,
}: {
  userId: string;
  onNavigate: () => void;
}) {
  const { data: user } = useUser(userId);
  return (
    <li className="py-2">
      <Link
        to={`/management/users/${userId}`}
        onClick={onNavigate}
        className="text-(--color-accent) hover:underline"
      >
        {user?.name ?? userId}
      </Link>
    </li>
  );
}
